import * as React from 'react';
import { Route, Routes, useNavigate, useLocation, } from "react-router-dom-v5-compat";

import LockedPage from './locked_page'
import Sidebar from './sidebar'
import HistoryAndRecommendations from './history_and_recommendations/index'
import { ClassroomInterface, SchoolAdminInterface, } from './interfaces'
import { NO_CLASSROOM, } from './shared'

import { SMALL_ICON_BASE_SRC, TEACHER, ADMIN, APPROVED, } from '../../../../Shared/index'
import useHideFooter from '../../../../Shared/hooks/useHideFooter'
import { PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, } from '../progress_report_constants';

const openSidebarIconSrc = `${SMALL_ICON_BASE_SRC}/sidebar-open.svg`
const closeSidebarIconSrc = `${SMALL_ICON_BASE_SRC}/sidebar-close.svg`

interface DiagnosticInsightsProps {
  classrooms: ClassroomInterface[];
  school: any;
  needsEmailVerification: boolean;
  adminVerificationStatus: string;
  teacherVerificationStatus: string;
  role: string;
  schoolAdmins: SchoolAdminInterface[];
  alternativeSchoolNames: string[];
}

const DiagnosticInsights = ({ classrooms, school, needsEmailVerification, adminVerificationStatus, teacherVerificationStatus, role, schoolAdmins, alternativeSchoolNames, }: DiagnosticInsightsProps) => {
  const [classroom, setClassroom] = React.useState(null)
  const [showSidebar, setShowSidebar] = React.useState(true)
  const [showMobileSidebar, setShowMobileSidebar] = React.useState(false)

  const navigate = useNavigate();
  const location = useLocation();

  useHideFooter()

  // admins who were created prior to the advent of the admin approval process do not have a verification value, but are treated as approved throughout the codebase
  const showLockedPage = needsEmailVerification || (role === TEACHER && teacherVerificationStatus !== APPROVED) || (role === ADMIN && adminVerificationStatus && adminVerificationStatus !== APPROVED)

  React.useEffect(() => {
    if (showLockedPage) { return }

    const localStorageSelectedClassroomId = window.localStorage.getItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID)
    const classroomFromLocalStorageClassroomId = classrooms.find(c => Number(c.id) === Number(localStorageSelectedClassroomId))
    setClassroom(classroomFromLocalStorageClassroomId || classrooms[0] || NO_CLASSROOM)
  }, [])

  React.useEffect(() => {
    if (!classroom) { return }
    window.localStorage.setItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, classroom.id)
  }, [classroom])

  React.useEffect(() => {
    if (location.pathname === '/teachers/progress_reports/diagnostic_insights' && classroom?.id) {
      navigate(`/teachers/progress_reports/diagnostic_insights/classroom/${classroom?.id}/history_and_recommendations/by_class/history`);
    }
  }, [location.pathname, navigate, classroom?.id]);

  function openMobileSidebar() { setShowMobileSidebar(true) }

  function closeMobileSidebar() { setShowMobileSidebar(false) }

  function toggleSidebar() { setShowSidebar(!showSidebar) }

  function renderShowSidebarButton() {
    const ariaLabel = showSidebar ? 'Close sidebar' : 'Open sidebar'

    const imgSrc = showSidebar ? closeSidebarIconSrc : openSidebarIconSrc

    return (
      <button
        aria-label={ariaLabel}
        className='interactive-wrapper focus-on-light show-sidebar-button'
        onClick={toggleSidebar}
        type="button"
      >
        <img alt="" src={imgSrc} />
      </button>
    )
  }

  let sidebar

  if (showSidebar) {
    sidebar = (
      <Sidebar
        classrooms={classrooms}
        closeMobileSidebar={closeMobileSidebar}
        selectedClassroomId={classroom?.id}
        setSelectedClassroom={setClassroom}
        showMobileSidebar={showMobileSidebar}
        showSidebarButton={renderShowSidebarButton()}
      />
    )
  }

  if (showLockedPage) {
    return (
      <LockedPage
        adminVerificationStatus={adminVerificationStatus}
        alternativeSchoolNames={alternativeSchoolNames}
        needsEmailVerification={needsEmailVerification}
        role={role}
        school={school}
        schoolAdmins={schoolAdmins}
        teacherVerificationStatus={teacherVerificationStatus}
      />
    )
  }

  return (
    <div className="diagnostic-insights-container">
      {sidebar}
      <div className={showSidebar ? 'sidebar-open' : 'sidebar-closed'}>
        {showSidebar ? null : renderShowSidebarButton()}
        <Routes>
          <Route element={<HistoryAndRecommendations classroom={classroom} needsEmailVerification={needsEmailVerification} teacherClassroomIds={classrooms.map(c => c.id)} />} path='/classroom/:classroomId/history_and_recommendations/*' />
        </Routes>
      </div>
    </div>
  )
}

export default DiagnosticInsights
